<template>
  <div class="new-criterio-content">
    <v-card class="new-criterio" elevation="0" :disabled="disabled">
      <v-card-text>
        <v-tabs v-model="tabIndex" background-color="transparent" hide-slider grow class="tabs-menu">
          <v-tab v-for="tab in criterias.children" :key="tab.id" class="tab-container" @change="changeTab(tab.children)">
            {{ tab.label }}
            <news-badge v-if="isNewSegmentCriterias(tab.label)" class="pl-1" />
          </v-tab>
        </v-tabs>
        <div class="segmentation-criteria-group">
          <v-tabs-items v-for="group in groups" :key="group.id">
            <p class="group-title">{{ group.label }}</p>
            <div v-for="(criterio, index) in group.children" :key="index" class="criterio-template">
              <div v-if="!criterio.hidden">
                <v-menu v-if="getSafe(() => segmentationGroups.groups.length) >= 2">
                  <template v-slot:activator="scope">
                    <v-btn text v-on:dblclick.once="scope.on.click" @dblclick="clickedCriteria = criterio">
                      <v-card
                        draggable
                        :class="{ dragged: draggedObject.id === index && group.id === groupId }"
                        @dragstart="drag(criterio, group)"
                        @dragend="dragEnd"
                      >
                        <v-card-text class="card-box">
                          <mf-icon class="drag-icon" small>drag_indicator</mf-icon>
                          <p>
                            {{ criterio.label }}
                            <news-badge
                              v-if="isNewSegmentCriterias(`${criterias.children[tabIndex].label}.${group.label}.${criterio.label}`)"
                              style="display: inline"
                            />
                          </p>
                          <mf-icon class="arrow-icon-right" small>arrow_forward_ios</mf-icon>
                        </v-card-text>
                      </v-card>
                    </v-btn>
                  </template>

                  <v-list>
                    <v-list-item
                      v-for="(groupToSelect, index) in segmentationGroups.groups"
                      :key="index"
                      @click="addCriteriaOnGroup(clickedCriteria, group, index)"
                    >
                      <v-list-item-title>Grupo {{ index + 1 }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>

                <v-card
                  v-else
                  class="criterio-container"
                  draggable
                  :class="{ dragged: draggedObject.id === index && group.id === groupId }"
                  @dragstart="drag(criterio, group)"
                  @dragend="dragEnd"
                  @dblclick="addCriteriaOnGroup(criterio, group, 0)"
                >
                  <mf-icon class="drag-icon" small>drag_indicator</mf-icon>
                  <v-card-text class="card-box">
                    <p>
                      {{ criterio.label }}
                      <news-badge
                        v-if="isNewSegmentCriterias(`${criterias.children[tabIndex].label}.${group.label}.${criterio.label}`)"
                        style="display: inline"
                      />
                    </p>
                  </v-card-text>
                  <mf-icon class="arrow-icon-right" small>arrow_forward_ios</mf-icon>
                </v-card>
              </div>
            </div>
          </v-tabs-items>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { mapWaitingActions } from 'vue-wait'
import { isNewSegmentCriterias } from '@/helpers/news.js'

import { leftBranch } from '@/helpers/segmentationTree'
import cloneDeep from 'lodash/cloneDeep'
export default {
  components: {
    NewsBadge: () => import('@/components/NewsBadge.vue'),
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    localGroups: [],
    group: null,
    clickedCriteria: null,
  }),
  computed: {
    ...mapState({
      draggedObject: (state) => state.segmentation.draggedObject,
      selectedCondition: (state) => state.segmentation.selectedCondition,
      criterias: (state) => state.segmentation.segmentation,
      segmentationGroups: (state) => state.segmentation.segmentationGroups,
      segmentationTabIndex: (state) => state.segmentation.segmentationTabIndex,
    }),

    tabIndex: {
      get() {
        return this.segmentationTabIndex
      },
      set(value) {
        this.setSegmentationTabIndex(value)
      },
    },
    groups: {
      get() {
        return this.localGroups
          .filter((group) => !group.requires_pii_access || this.hasPiiAccess)
          .filter((group) => !group.hidden)
          .filter((group) => group.children?.filter((child) => !child.hidden).length > 0)
      },
      set(groups) {
        this.localGroups = groups
      },
    },
  },
  mounted() {
    this.groups = this.criterias.children[this.tabIndex].children
  },
  methods: {
    ...mapWaitingActions({
      setDraggedObject: 'set-dragged-object',
      setSelectedCriteria: 'set-selected-criteria',
      setSegmentationGroups: 'set-segmentation-groups',
      setSegmentationTabIndex: 'set-segmentation-tab-index',
    }),
    isNewSegmentCriterias,
    drag(criterio, group) {
      this.group = group
      const newLeftBranch = leftBranch(criterio.children[0]).map((node) => ({
        id: node.id,
        value: node.value,
      }))

      const newCriteriaValue = [{ id: this.criterias.children[this.tabIndex].id }, { id: group.id }, { id: criterio.id }]

      const draggedObject = { criteriaValue: newCriteriaValue.concat(newLeftBranch), new: true }

      this.setDraggedObject(draggedObject)
    },

    dragEnd() {
      this.group = null
      this.setDraggedObject({})
    },

    addCriteriaOnGroup(criterio, criteriaGroup, groupIndex) {
      this.group = criteriaGroup
      const newLeftBranch = leftBranch(criterio.children[0]).map((node) => ({
        id: node.id,
        value: node.value,
      }))

      const newCriteriaValue = [{ id: this.criterias.children[this.tabIndex].id }, { id: criteriaGroup.id }, { id: criterio.id }]

      const criterioToAdd = { criteriaValue: newCriteriaValue.concat(newLeftBranch), new: true }

      this.setSelectedCriteria({ ...criterioToAdd, groupIndex, criteriaIndex: this.getIndexToInsertCriteria(groupIndex) })

      this.insertCriteriaOnGroup(criterioToAdd.criteriaValue, groupIndex, this.getIndexToInsertCriteria(groupIndex))
    },

    getIndexToInsertCriteria(groupIndex) {
      const branch = this.segmentationGroups.groups[groupIndex]

      if (branch?.criteria?.length) return branch.criteria.length
      else return 0
    },

    insertCriteriaOnGroup(branch, groupIndex, criteriaIndex) {
      let segmentationGroups = cloneDeep(this.segmentationGroups)
      if (groupIndex >= segmentationGroups?.groups?.length) {
        segmentationGroups.groups[groupIndex] = {
          criteria: [],
          operator: 'AND',
        }
      }

      segmentationGroups.groups[groupIndex].criteria[criteriaIndex] = branch
      this.setSegmentationGroups(segmentationGroups)
    },

    changeTab(groups) {
      this.groups = groups
    },
  },
}
</script>

<style lang="scss" scoped>
.new-criterio-content {
  .instructions-container {
    background-color: $marca-0-l30;
    border-radius: 10px;
    padding: 10px;
    margin: 0 20px !important;
    color: $branco;
    font-size: 14px;
    font-weight: bold;
    align-items: center;
  }

  .new-criterio {
    background-color: #f3f3f3 !important;

    .v-card__text {
      padding-top: 0;
    }

    .tabs-menu {
      border-bottom: 1px solid $cinza-l60;
    }
    .segmentation-criteria-group {
      height: 450px;
      overflow-y: auto;
      overflow-x: hidden;

      ::v-deep .v-window__container {
        background-color: $cinza-l90;
      }
    }

    .tab-container {
      font-size: 12px;
      padding: 0 4px;
      text-align: center;
      border-bottom: 3px transparent solid;

      ::v-deep .v-slide-group__next,
      ::v-deep .v-slide-group__next {
        max-width: 35px;
      }

      &.v-tab--active {
        color: $cinza-d20;
        border-bottom: 3px $marca-0-l40 solid;
        background-color: transparent;
      }
    }
    .group-title {
      color: $cinza-l30;
      font-weight: bold;
      text-transform: uppercase;
      font-size: 12px;
      margin: 15px 0;
    }

    .criterio-container {
      height: 28px;
      background-color: $branco;
      border: #dbdbdb 1px solid !important;
      margin-bottom: 8px;
      cursor: move;

      p {
        font-size: 12px;
      }
    }

    .criterio-template {
      text-align: left;
      margin-bottom: 6px;

      ::v-deep .v-card__text {
        height: 28px;
      }

      button {
        padding: 0;
        height: 28px;
        width: 100%;

        .v-card {
          width: 100%;
        }
      }

      .v-btn {
        text-transform: none;
      }
    }

    .card-box {
      padding: 3px 5px 5px 30px;
      p {
        margin: 0;
        text-align: left;
        font-size: 12px;
        margin: 0 !important;
      }
    }
    .drag-icon {
      position: absolute;
      left: 5px;
      top: 50%;
      transform: translateY(-50%);
      color: $cinza-l70;
    }
    .arrow-icon-right {
      position: absolute;
      right: 5px;
      top: 50%;
      transform: translateY(-50%);
      color: $cinza-l70;
    }

    .dragged {
      background-color: $cinza-l80 !important;
    }
  }
}
</style>
